import { Trans, useTranslation } from "react-i18next";

/** Keys under `home.hero.stats`; each has a `value` and a `label`. */
const stats = ["output", "models", "render", "uptime"];

export default function Hero() {
  const { t } = useTranslation();
  return (
    <section id="top" className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Holographic background image + tint */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20futuristic%20holographic%20background%20with%20flowing%20translucent%20teal%20and%20cyan%20gradient%20waves%2C%20soft%20glowing%20neon%20light%20streaks%2C%20floating%20frosted%20glass%20panels%2C%20cinematic%20volumetric%20lighting%2C%20deep%20dark%20teal%20backdrop%2C%20premium%20medical%20technology%20aesthetic%2C%20ultra%20clean%20minimal%20composition%2C%20high%20contrast%2C%20elegant%20sophisticated&width=1600&height=900&seq=hero-bg-holo&orientation=landscape"
          alt={t("home.hero.bgAlt")}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/85 via-primary-800/70 to-accent-700/80"></div>
        <div className="absolute -top-32 -left-24 w-[560px] h-[560px] rounded-full bg-accent-400/25 blur-3xl animate-float-slow"></div>
        <div className="absolute top-1/4 -right-24 w-[480px] h-[480px] rounded-full bg-primary-400/25 blur-3xl animate-float"></div>
        <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-accent-300/20 blur-3xl animate-float-x"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-background-50"></div>
      </div>

      {/* Floating holographic UI */}
      <div className="pointer-events-none absolute inset-0 hidden lg:block">
        <div className="absolute top-[22%] left-[8%] glass-dark rounded-xl px-4 py-3 animate-float">
          <div className="flex items-center gap-2">
            <i className="ri-focus-3-line text-accent-300 text-lg"></i>
            <span className="text-background-50/90 text-sm font-medium">{t("home.hero.chipOutput")}</span>
          </div>
        </div>
        <div className="absolute top-[38%] right-[9%] glass-dark rounded-xl px-4 py-3 animate-float-slow">
          <div className="flex items-center gap-2">
            <i className="ri-cpu-line text-accent-300 text-lg"></i>
            <span className="text-background-50/90 text-sm font-medium">{t("home.hero.chipEngine")}</span>
          </div>
        </div>
        <div className="absolute bottom-[28%] left-[14%] glass-dark rounded-xl px-4 py-3 animate-float-x">
          <div className="flex items-center gap-2">
            <i className="ri-magic-line text-accent-300 text-lg"></i>
            <span className="text-background-50/90 text-sm font-medium">{t("home.hero.chipRender")}</span>
          </div>
        </div>
        <div className="absolute top-[24%] right-[24%] w-24 h-24 rounded-full border border-accent-400/30 animate-spin-slow">
          <span className="absolute top-2 left-2 w-2 h-2 rounded-full bg-accent-300 animate-glow"></span>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full flex-1 flex flex-col items-center justify-center text-center px-4 md:px-6 pt-28 pb-16">
        <span className="inline-flex items-center gap-2 glass-dark rounded-full px-4 py-1.5 text-xs font-medium text-background-50/90 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-300 animate-glow"></span>
          {t("home.hero.badge")}
        </span>

        <h1 className="w-full max-w-4xl text-4xl md:text-6xl font-bold text-background-50 leading-tight tracking-tight">
          <Trans i18nKey="home.hero.title" components={{ holo: <span className="holo-text" /> }} />
        </h1>

        <p className="mt-6 max-w-2xl text-base md:text-lg text-background-50/80">
          {t("home.hero.subtitle")}
        </p>

        <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
          <a
            href="#demo"
            className="w-full sm:w-auto whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 px-8 py-4 text-base font-semibold text-background-50 transition-transform hover:scale-[1.04] cursor-pointer"
          >
            {t("common.nav.generateNow")}
          </a>
          <a
            href="#pricing"
            className="w-full sm:w-auto whitespace-nowrap rounded-full glass px-8 py-4 text-base font-semibold text-background-50 transition-colors hover:bg-white/15 cursor-pointer"
          >
            {t("home.hero.viewPricing")}
          </a>
        </div>

        <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-8 w-full max-w-3xl">
          {stats.map((id) => (
            <div key={id} className="glass-dark rounded-lg px-3 py-4">
              <div className="text-2xl md:text-3xl font-bold text-background-50">{t(`home.hero.stats.${id}.value`)}</div>
              <div className="text-xs text-background-50/70 mt-1">{t(`home.hero.stats.${id}.label`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}